import { useEffect, useState } from "react";
import { useTabTitle } from "@/lib/tabs";
import { GripVertical, Plus, Tag as TagIcon, Trash2 } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { EmptyState, PageBody, PageHeader } from "@/components/PageChrome";

/** Org-wide Tags manager (Library › Tags). Tags are shared across every collection. */
export default function LibraryTagsPage() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", color: "" });
  const [q, setQ] = useState("");

  useTabTitle("Tags", "tag");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const persistOrder = async (ordered) => {
    const prev = tags;
    setTags(ordered);
    try {
      await api.post("/tags/reorder", { order: ordered.map((t) => t.id) });
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setTags(prev);
    }
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = tags.findIndex((t) => t.id === active.id);
    const to = tags.findIndex((t) => t.id === over.id);
    if (from === -1 || to === -1) return;
    persistOrder(arrayMove(tags, from, to));
  };

  const load = async () => {
    try {
      const tagRes = await api.get("/tags");
      setTags(tagRes.data);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createTag = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tags", {
        name: form.name,
        entity_type_id: null, // library tags are always org-wide
        color: form.color || null,
      });
      toast.success("Tag created");
      setOpen(false);
      setForm({ name: "", color: "" });
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tag "${t.name}"?\nIt will be removed from ${t.usage_count} item(s). This action cannot be undone.`)) return;
    try {
      await api.delete(`/tags/${t.id}`);
      toast.success("Tag deleted");
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const filtered = q ? tags.filter((t) => t.name.toLowerCase().includes(q.toLowerCase())) : tags;

  return (
    <>
      <PageHeader
        title="Tags"
        subtitle="Lightweight labels shared across every collection. Create a tag once here and use it anywhere."
        breadcrumbs={[{ label: "Library" }, { label: "Tags" }]}
        actions={
          <div className="flex gap-2">
            <Input placeholder="Filter…" className="w-[200px]" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tag-filter" />
            <Button onClick={() => setOpen(true)} data-testid="new-tag-btn"><Plus className="w-4 h-4 mr-1.5" /> New tag</Button>
          </div>
        }
      />
      <PageBody>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={TagIcon}
            title={q ? "No tags match" : "No tags yet"}
            description={q ? "Try a different filter." : "Create your first tag to start labeling items."}
            action={!q && <Button onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1.5" /> New tag</Button>}
          />
        ) : (
          <div className="rounded-lg border border-border bg-white overflow-hidden">
            {!q && (
              <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-b border-border/60">
                Drag the handle to set the order tags appear in.
              </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <Table>
                <TableHeader>
                  <TableRow>
                    {!q && <TableHead className="w-8" />}
                    <TableHead>Tag</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {q ? (
                    filtered.map((t) => (
                      <TableRow key={t.id} data-testid={`tag-row-${t.slug}`}>
                        <TagChipCell tag={t} />
                        <UsageCell tag={t} />
                        <ActionsCell tag={t} onDelete={remove} />
                      </TableRow>
                    ))
                  ) : (
                    <SortableContext items={tags.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                      {tags.map((t) => (
                        <SortableTagRow key={t.id} tag={t} onDelete={remove} />
                      ))}
                    </SortableContext>
                  )}
                </TableBody>
              </Table>
            </DndContext>
          </div>
        )}
      </PageBody>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-tag-dialog">
          <form onSubmit={createTag}>
            <DialogHeader>
              <DialogTitle>New tag</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  autoFocus required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="input-tag-name"
                />
              </div>
              <div>
                <Label>Color (optional)</Label>
                <Input
                  placeholder="#0d9488" value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-tag">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TagChipCell({ tag: t }) {
  return (
    <TableCell>
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: (t.color || "#0d9488") + "1a", color: t.color || "#0d9488" }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.color || "#0d9488" }} />
        {t.name}
      </span>
    </TableCell>
  );
}

function UsageCell({ tag: t }) {
  return (
    <TableCell className="font-mono text-xs">
      {t.usage_count} record{t.usage_count === 1 ? "" : "s"}
    </TableCell>
  );
}

function ActionsCell({ tag: t, onDelete }) {
  return (
    <TableCell className="text-right">
      <Button
        variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
        onClick={() => onDelete(t)} data-testid={`delete-tag-${t.slug}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </TableCell>
  );
}

function SortableTagRow({ tag: t, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: t.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`tag-row-${t.slug}`}>
      <TableCell className="w-8 pr-0">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none p-1"
          aria-label="Drag to reorder"
          data-testid={`tag-drag-${t.slug}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </TableCell>
      <TagChipCell tag={t} />
      <UsageCell tag={t} />
      <ActionsCell tag={t} onDelete={onDelete} />
    </TableRow>
  );
}
